import React from 'react';
import { connect } from 'react-redux';
import { Route, RouteComponentProps, Switch } from 'react-router-dom';

import { IntegrationsSubspaceName } from '~src/app/spaces/IntegrationsSettings/IntegrationsSiteSettingsSubspace';
import { Projects } from '~src/projects/resources/Projects';
import { AllSchedulesSpaceProvider } from '~src/schedule/components/AllSchedulesSpaceProvider';
import { ScheduleSpaceRouter } from '~src/schedule/components/ScheduleRouter';
import { ConnectedSearchMicroViewHost } from '~src/search/components/SearchMicroViewHost';
import { SearchSpaceRouter } from '~src/search/components/SearchSpaceRouter';

import { BackgroundJobsSpaceProvider } from '../../../backgroundJob/components/BackgroundJobsSpaceProvider';
import { ConnectedCatalogBrowserHost } from '../../../catalog/ConnectedCatalogBrowserHost';
import { DataRoleSpaceRouter } from '../../../dataRole/components/DataRoleSpaceRouter';
import { RedirectToFavoritedDataRoleProvider } from '../../../dataRole/components/RedirectToFavoritedDataRoleProvider';
import { DatasourceSpaceRouter } from '../../../datasource/components/DatasourceSpaceRouter';
import { ExploreDatasourceProvider } from '../../../datasource/components/ExploreDatasourceProvider';
import { RedirectToFavoritedDatasourceProvider } from '../../../datasource/components/RedirectToFavoritedDatasourceProvider';
import { AddUserToDataAlertProvider, RemoveUserFromDataAlertProvider } from '../../../dda/components/AddOrRemoveFromDataAlertProvider';
import { FlowSpaceFavoriteRedirect } from '../../../flow/components/FlowSpaceFavoriteRedirect';
import { FlowSpaceRouter } from '../../../flow/components/FlowSpaceRouter';
import { HomeSpaceProvider } from '../../../home/components/HomeSpaceProvider';
import { LensSpaceRouter } from '../../../lens/LensSpaceRouter';
import { ConnectedMetricDetailSpace, renderMetricDetailSpace, showResumeMetricWarning, showSuspendedMetricWarning, userCanResumeMetric } from '../../../metrics/components/MetricsSpace/MetricDetailSpace';
import { RedirectToFavoritedMetricProvider } from '../../../metrics/components/MetricsSpace/RedirectToFavoritedMetricProvider';
import { PublishedConnectionSpaceFavoriteRedirect } from '../../../publishedConnection/components/PublishedConnectionSpaceFavoriteRedirect';
import { PublishedConnectionSpaceRouter } from '../../../publishedConnection/components/PublishedConnectionSpaceRouter';
import { AuthLevel, getSessionManagementServiceInstance, SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { ServerSettingsUnauthenticated } from '../../../settings/services/ServerSettingsUnauthenticated';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { RedirectToFavoritedWorkbookProvider } from '../../../workbook/components/RedirectToFavoritedWorkbookProvider';
import { WorkbookSpaceRouter } from '../../../workbook/components/WorkbookSpaceRouter';
import { selectPreviousUrl } from '../../selectors/NavigationStateSelectors';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';
import { AppState } from '../../state/AppState';
import { SiteSpaceContentType } from '../../state/SiteSpaceContentTypeState';
import { SiteSettingsSubspaceName } from '../../state/SpaceInfo';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater } from '../AnalysisVizSpace/AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater';
import { CollectionsSpaceRouter } from '../Collections/CollectionsSpaceRouter';
import { ConnectedFavoritesSpace } from '../FavoritesSpace/ConnectedFavoritesSpace';
import { AllGroupsSpaceProvider } from '../groups/AllGroupsSpaceProvider';
import { GroupSpaceRouter } from '../groups/GroupSpaceRouter';
import { PersonalSpaceProvider } from '../PersonalSpace/PersonalSpaceProvider';
import { ProjectSpaceRouter } from '../ProjectSpace/ProjectSpaceRouter';
import { RedirectToFavoritedProjectProvider } from '../ProjectSpace/RedirectToFavoritedProjectProvider';
import { RecentsSpaceProvider } from '../RecentsSpace/RecentsSpaceProvider';
import { RecommendationsSpaceProvider } from '../RecommendationSpace/RecommendationsSpaceProvider';
import { SharedWithMeSpaceProvider } from '../SharedWithMe/SharedWithMeSpaceProvider';
import { isIntegrationsTabEnabledForSiteSettings } from '../SiteSettingsSpace/SiteSettingsSpace';
import { ConnectedSiteSettingsSpaceProvider as SiteSettingsSpaceProvider } from '../SiteSettingsSpace/SiteSettingsSpaceProvider';
import { SiteSpaceProvider, SiteSpaceProviderProps } from '../SiteSpace/SiteSpaceProvider';
import { SiteStatusSpaceProvider } from '../SiteStatusSpace/SiteStatusSpaceProvider';
import { SiteTasksSpaceRouter } from '../SiteTasksSpace/SiteTasksSpaceRouter';
import { SiteUserSpaceRouter } from '../SiteUserSpace/SiteUserSpaceRouter';
import { AllSiteUsersSpaceProvider } from '../SiteUsersSpace/AllSiteUsersSpaceProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { StartersSpaceProvider } from '../Starters/StartersSpaceProvider';
import { AllSiteUsersAndIdentitiesForSingleSiteSpaceRouter } from '../UserIdentitiesSpace/SiteUsers/AllSiteUsersAndIdentitiesForSingleSiteSpaceRouter';
import { ConnectedRedirectToViewSpaceProvider, RedirectViewType } from '../VizSpace/RedirectToViewProvider';
import { VizSpaceUrlUpdater } from '../VizSpace/VizSpaceUrlUpdater';
import { VizUrl } from '../VizSpace/VizUrlHelpers';
import { ConnectedReduxRouterErrorWrapper } from './ReduxRouterErrorWrapper';
import { RouterError } from './RouterError';

export interface SiteSpaceRouterOwnProps {
  readonly siteName: string;
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
}

export interface SiteSpaceRouterStateProps {
  readonly previousUrl: string;
  readonly urls: SpacesSiteUrls;
  readonly routerReactKey: number;
}

export type SiteSpaceRouterComponentProps = SiteSpaceRouterOwnProps & SiteSpaceRouterStateProps;

export class SiteSpaceRouterComponent extends React.PureComponent<SiteSpaceRouterComponentProps> {
  private server: SessionManagementService;

  public constructor(props: SiteSpaceRouterComponentProps) {
    super(props);
    this.server = getSessionManagementServiceInstance();
  }

  public render() {
    return (
      <Switch>
        <Route path={this.props.urls.searchPath()} render={() => (FeatureFlags.searchV2
          ? <ConnectedSearchMicroViewHost />
          : <SearchSpaceRouter {...this.props} />)} />
        <Route exact path={this.props.urls.dataRolesPath()} render={this.siteSpaceprovider('dataRole')} />
        <Route exact path={this.props.urls.projectsPath()} render={this.siteSpaceprovider('project')} />
        <Route exact path={this.props.urls.explorePath()} render={this.siteSpaceprovider('explore')} />
        {this.server.flowsOnSiteEnabled() &&
          <Route exact path={this.props.urls.flowsPath()} render={this.siteSpaceprovider('flow')} />
        }
        {FeatureFlags.publishedConnectionContentManagementEnabled &&
          <Route exact path={this.props.urls.publishedConnectionsPath()} render={this.siteSpaceprovider('publishedConnection')} />
        }
        {FeatureFlags.nlplensContentTypeEnabled &&
          <Route exact path={this.props.urls.lensesPath()} render={this.siteSpaceprovider('lens')} />
        }
        <Route exact path={this.props.urls.viewsPath()} render={this.siteSpaceprovider('view')} />
        {
          FeatureFlags.metricsSpaceEnabled &&
          <Route exact path={this.props.urls.metricsPath()} render={this.siteSpaceprovider('metric')} />
        }

        {this.getVizRoute()}
        <Route path={this.props.urls.redirectToFavoriteViewPath(':id')} render={({ match }) => this.redirectToViewProvider(match.params.id, 'favorite')} />
        <Route path={this.props.urls.redirectToViewPath(':id')} render={({ match }) => this.redirectToViewProvider(match.params.id, 'regular')} />
        {
          !FeatureFlags.removeUsernamesFromCVURLEnabled &&
          <Route path={this.props.urls.redirectToCustomViewPath(':viewId', ':username', ':customViewUrlId')} render={({ match }) => this.redirectToViewProvider(match.params.viewId, 'customView', match.params.username, match.params.customViewUrlId)} />
        }
        {
          FeatureFlags.removeUsernamesFromCVURLEnabled &&
          <Route path={this.props.urls.redirectToCustomViewPathWithLuid(':viewId', ':customViewLuid', ':customViewUrlId')} render={({ match }) => this.redirectToViewProvider(match.params.viewId, 'customView', undefined, match.params.customViewUrlId, match.params.customViewLuid)} />
        }


        <Route exact path={this.props.urls.workbooksPath()} render={this.siteSpaceprovider('workbook')} />
        <Route path={this.props.urls.workbookPath(':id')} render={() => <WorkbookSpaceRouter {...this.props} />} />
        <Route exact path={this.props.urls.redirectToWorkbookPath(':id')} render={({ match }) => this.redirectToFavoritedWorkbookProvider(match.params.id)} />

        {this.getAllGroupsRoute(this.server)}

        {FeatureFlags.collectionsEnabled && this.getCollectionsRoute()}

        <Route exact path={this.props.urls.datasourcesPath()} render={this.siteSpaceprovider('datasource')} />
        <Route path={this.props.urls.datasourcePath(':id')} render={() => <DatasourceSpaceRouter {...this.props} />} />
        {/* // explore/datasource navigates to the data source by its Published Datasource ID, rather than the Unified Datasource (connection) ID */}
        <Route exact path={this.props.urls.exploreDatasourcePath(':id')} render={({ match }) => this.exploreDatasourceProvider(match.params.id)} />
        <Route exact path={this.props.urls.favoriteDatasourcePath(':id', ':idForFavoriting')} render={({ match }) => this.redirectToFavoritedDatasourceProvider(match.params.idForFavoriting, match.params.id)} />
        <Route exact path={this.props.urls.favoriteDatasourceDoesntExistPath(':idForFavoriting')} render={({ match }) => this.redirectToFavoritedDatasourceProvider(match.params.idForFavoriting)} />

        <Route path={this.props.urls.flowPath(':id')} render={() => <FlowSpaceRouter {...this.props} />} />
        {
          FeatureFlags.publishedConnectionContentManagementEnabled &&
          <Route path={this.props.urls.publishedConnectionPath(':id')} render={() => <PublishedConnectionSpaceRouter {...this.props} />} />
        }
        {
          FeatureFlags.publishedConnectionContentManagementEnabled &&
          <Route exact path={this.props.urls.redirectToPublishedConnectionPath(':id')} render={({ match }) => this.redirectToPublishedConnectionProvider(match.params.id)} />

        }

        <Route exact path={this.props.urls.redirectToFlowPath(':id')} render={({ match }) => this.redirectToFlowProvider(match.params.id)} />

        {FeatureFlags.metricsSpaceEnabled &&
          <Route path={this.props.urls.metricPath(':id')} render={({ match }) =>
            <ConnectedMetricDetailSpace
              siteName={this.props.siteName}
              id={match.params.id}
              urls={this.props.urls}
              renderMetricDetailSpace={renderMetricDetailSpace}
              showResumeMetricWarning={showResumeMetricWarning}
              showSuspendedMetricWarning={showSuspendedMetricWarning}
              userCanResumeMetric={userCanResumeMetric}
              routeLocationSearch={this.props.routeLocationSearch}
              projects={Projects}
            />
          } />
        }
        {FeatureFlags.metricsSpaceEnabled &&
          <Route path={this.props.urls.redirectToMetricPath(':id')} render={({ match }) =>
            <RedirectToFavoritedMetricProvider metricId={match.params.id} urls={this.props.urls} />
          } />
        }
        {this.getUsersRoute(this.server)}
        <Route path={this.props.urls.baseUserPath()} render={() => <SiteUserSpaceRouter {...this.props} />} />

        <Route path={this.props.urls.projectPath(':id')} render={() => <ProjectSpaceRouter {...this.props} />} />
        <Route exact path={this.props.urls.redirectToProjectPath(':id')} render={({ match }) => this.redirectToFavoritedProjectProvider(match.params.id)} />

        <Route path={this.props.urls.favoritesPath()} render={this.favoritesSpace} />
        <Route path={this.props.urls.recentsPath()} render={this.recentsSpace} />
        {FeatureFlags.homePage &&
          <Route path={this.props.urls.homePath()} render={() => <HomeSpaceProvider {...this.props} />} />
        }
        <Route path={this.props.urls.recommendationListPath()} render={() => <RecommendationsSpaceProvider {...this.props} />} />

        <Route path={this.props.urls.dataRolePath(':id')} render={() => <DataRoleSpaceRouter {...this.props} />} />
        <Route path={this.props.urls.redirectToDataRolePath(':idForFavoriting')} render={({ match }) => this.redirectToFavoritedDataRoleProvider(match.params.idForFavoriting)} />
        {
          FeatureFlags.nlplensContentTypeEnabled &&
          <Route path={this.props.urls.lensPath(':luid')} render={() => <LensSpaceRouter {...this.props} />} />
        }
        {this.getSiteStatusRoute(this.server)}
        {this.getSiteStatusVizRoute(this.server)}

        {this.getSiteTasksRoute(this.server)}
        {
          FeatureFlags.jobManagementEnabled &&
          this.getSiteBackgroundJobsRoute(this.server)
        }
        {
          this.getDataCatalogRoute(this.server)
        }
        <Route path={this.props.urls.addUserToDataAlertPath(':dataAlertId')} render={({ match }) => <AddUserToDataAlertProvider dataAlertId={match.params.dataAlertId} />} />
        <Route path={this.props.urls.removeUserFromDataAlertPath(':dataAlertId')} render={({ match }) => <RemoveUserFromDataAlertProvider dataAlertId={match.params.dataAlertId} />} />

        {this.getGroupRoute(this.server)}
        {this.getScheduleRoute(this.server)}
        {this.getAllSchedulesRoute(this.server)}
        {this.settingsRoute(this.props.urls.settingsPath(), 'settings')}
        {FeatureFlags.extensionsSettingsTabEnabled && this.settingsRoute(this.props.urls.settingsExtensionsPath(), 'extensions')}
        {
          ServerSettingsUnauthenticated.isSiteSamlEnabled() &&
          this.settingsRoute(this.props.urls.settingsAuthenticationPath(), 'authentication')
        }
        {
          ServerSettingsUnauthenticated.isBridgeEnabled() &&
          this.settingsRoute(this.props.urls.settingsBridgePath(), 'bridge')
        }
        {FeatureFlags.startersChannelEnabled && this.getStartersRouteByConnectionType()}
        {FeatureFlags.sharedWithMeBasicEnabled && this.server.isNamedSharingEnabled() && this.getSharedWithMeRoute()}
        {FeatureFlags.personalSpaceEnabled && this.getPersonalSpaceRoute(this.server)}
        {isIntegrationsTabEnabledForSiteSettings() && this.settingsRoute(this.props.urls.settingsIntegrationsPath(), IntegrationsSubspaceName as SiteSettingsSubspaceName)}
        <Route render={this.error} />
      </Switch>
    );
  }

  private settingsRoute(path: string, subspaceName: SiteSettingsSubspaceName) {
    // isSiteAdminSettingsEnabled is set on the backend (true for online and false for on-prem),
    // so that site admin can access settings only in online environment.
    if (this.server.isSessionServerAdmin()
      || (this.server.isSessionSiteAdmin() && this.server.isSiteAdminSettingsEnabled())) {
      return <Route exact path={path} render={this.settingsProvider(subspaceName)} />;
    }
  }

  private settingsProvider(subspaceName: SiteSettingsSubspaceName) {
    return () =>
      <SiteSettingsSpaceProvider
        urls={this.props.urls}
        siteName={this.props.siteName}
        subspaceName={subspaceName}
      />;
  }

  private getDataCatalogRoute(server: SessionManagementService) {
    return server.catalogingOnSiteEnabled() &&
      <Route path={this.props.urls.catalogPath()}
        render={() => <ConnectedCatalogBrowserHost />}
      />;
  }

  private getSiteBackgroundJobsRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.jobsPath()}
        render={(routeProps) => <BackgroundJobsSpaceProvider
          siteName={this.props.siteName}
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
        />}
      />;
  }

  private getAllSchedulesRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route exact path={this.props.urls.schedulesPath()}
        render={(routeProps) => <AllSchedulesSpaceProvider
          siteName={this.props.siteName}
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
          urls={this.props.urls}
        />}
      />;
  }

  private getCollectionsRoute() {
    return <Route
      path={this.props.urls.collectionsPath()}
      render={() =>
        <CollectionsSpaceRouter
          urls={this.props.urls}
          siteName={this.props.siteName}
        />}
    />;
  }

  private getAllGroupsRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route exact path={this.props.urls.groupsPath()}
        render={(routeProps) => <AllGroupsSpaceProvider
          siteName={this.props.siteName}
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
          urls={this.props.urls}
        />}
      />;
  }

  private getScheduleRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.schedulePath(':id')} render={() => <ScheduleSpaceRouter {...this.props} />} />;
  }

  private getGroupRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.groupPath(':id')} render={() => <GroupSpaceRouter {...this.props} />} />;
  }

  private siteSpaceprovider = (siteSpaceContentType: SiteSpaceContentType): (props: RouteComponentProps) => JSX.Element => {
    return (props: RouteComponentProps): JSX.Element => {
      const siteSpaceProviderProps: SiteSpaceProviderProps = {
        siteName: this.props.siteName,
        siteSpaceContentType,
        routeLocationSearch: props.location.search,
        routeLocationPath: props.location.pathname,
        urls: this.props.urls,
      };

      return <SiteSpaceProvider {...siteSpaceProviderProps} key={'1'} />;
    };
  }

  private getSiteStatusVizRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.siteStatusVizPath(':sheetUrlId')}
        render={(routeProps) => <AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater.ConnectedUpdater
          sheetUrlId={routeProps.match.params.sheetUrlId}
          routeLocationSearch={routeProps.location.search}
          history={routeProps.history}
          urls={this.props.urls}
          isServerAnalysis={false}
          renderSpaceProvider={AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater.renderProvider}
        />}
      />;
  }

  private getVizRoute() {
    return <Route path={this.props.urls.viewPath(':viewPath')}
      render={(routeProps) => {
        return (<VizSpaceUrlUpdater.ConnectedContainer
          vizPathFromUrl={VizUrl.getVizPathFromUrlLocation(routeProps.location)}
          routeLocationSearch={routeProps.location.search}
          urls={this.props.urls}
          renderProvider={VizSpaceUrlUpdater.renderProvider}
        />);
      }}
    />;
  }

  private favoritesSpace = (props: RouteComponentProps): JSX.Element => (
    <ConnectedFavoritesSpace
      siteName={this.props.siteName}
      routeLocationPath={props.location.pathname}
      routeLocationSearch={props.location.search}
    />
  )

  private recentsSpace = (props: RouteComponentProps): JSX.Element => (
    <RecentsSpaceProvider
      siteName={this.props.siteName}
      routeLocationPath={props.location.pathname}
      routeLocationSearch={props.location.search}
    />
  )


  private exploreDatasourceProvider = (publishedDatasourceId: string): JSX.Element => (
    <ExploreDatasourceProvider publishedDatasourceId={publishedDatasourceId} urls={this.props.urls} />
  )

  private redirectToFavoritedDataRoleProvider = (roleId?: string): JSX.Element => (
    <RedirectToFavoritedDataRoleProvider roleId={roleId} urls={this.props.urls} />
  )

  private redirectToFavoritedDatasourceProvider = (idForFavoriting: string, datasourceId?: string): JSX.Element => (
    <RedirectToFavoritedDatasourceProvider idForFavoriting={idForFavoriting} datasourceId={datasourceId} urls={this.props.urls} />
  )

  private redirectToFavoritedWorkbookProvider = (workbookId?: string): JSX.Element => (
    <RedirectToFavoritedWorkbookProvider workbookId={workbookId} urls={this.props.urls} />
  )

  private redirectToFavoritedProjectProvider = (projectId?: string): JSX.Element => (
    <RedirectToFavoritedProjectProvider projectId={projectId} urls={this.props.urls} />
  )

  private redirectToViewProvider = (viewId: string, redirectType: RedirectViewType, username?: string, customViewUrlId?: string, customViewLuid?: string): JSX.Element => (
    <ConnectedRedirectToViewSpaceProvider viewId={viewId} username={username} customViewUrlId={customViewUrlId} urls={this.props.urls} redirectType={redirectType} customViewLuid={customViewLuid} />
  )

  private redirectToFlowProvider = (flowId: string) => (
    <FlowSpaceFavoriteRedirect flowId={flowId} urls={this.props.urls} />
  )

  private redirectToPublishedConnectionProvider = (publishedConnectionId: string) => (
    <PublishedConnectionSpaceFavoriteRedirect publishedConnectionId={publishedConnectionId} urls={this.props.urls} />
  )

  private error = (): JSX.Element => {
    return <RouterError errorType={'default'} urls={this.props.urls} />;
  }

  private getSiteStatusRoute(server: SessionManagementService): React.ReactNode {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route
        path={this.props.urls.statusPath()}
        render={() =>
          <SiteStatusSpaceProvider
            siteName={this.props.siteName}
            urls={this.props.urls}
          />
        }
      />;
  }

  private getUsersRoute(server: SessionManagementService): React.ReactNode {
    if (FeatureFlags.identityMigrationEnabled && server.isSingleSiteOnPremServer() &&
          this.server.isUserAuthorized(AuthLevel.ServerAdmin)) {
      // For SingleSite OnPrem we split the Users space into two subspaces, Site Users and Identities.
      return <Route path={this.props.urls.usersPath()} render={(routeProps) =>
        <AllSiteUsersAndIdentitiesForSingleSiteSpaceRouter
          routeLocationPath={routeProps.location.pathname}
          routeLocationSearch={routeProps.location.search}
          siteName={this.props.siteName}
          urls={this.props.urls}
        />}
      />;
    } else {
      return this.server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.usersPath()} render={(routeProps) =>
        <AllSiteUsersSpaceProvider
          routeLocationPath={routeProps.location.pathname}
          routeLocationSearch={routeProps.location.search}
          siteName={this.props.siteName}
          urls={this.props.urls}
        />}
      />;
    }
  }

  private getSharedWithMeRoute(): React.ReactNode {
    return <Route path={this.props.urls.sharedWithMePath()}
      render={(routeProps) => <SharedWithMeSpaceProvider
        routeLocationSearch={routeProps.location.search}
        routeLocationPath={routeProps.location.pathname}
      />}
    />;
  }

  private getPersonalSpaceRoute(server: SessionManagementService): React.ReactNode {
    return server.shouldShowPersonalSpace() &&
      <Route path={this.props.urls.personalSpacePath()}
        render={(routeProps) => <PersonalSpaceProvider
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
        />}
      />;
  }

  private getStartersRouteByConnectionType() {
    return <Route
      exact path={this.props.urls.startersByConnectionTypePath(':starters')}
      render={(routeProps) => <StartersSpaceProvider
        routeLocationSearch={routeProps.location.search}
        routeLocationPath={routeProps.location.pathname}
        urls={this.props.urls}
        connectionType={routeProps.match.params.starters}
      />}
    />;
  }

  private getSiteTasksRoute(server: SessionManagementService): React.ReactNode {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.tasksPath()} render={() => <SiteTasksSpaceRouter {...this.props} />} />;
  }
}

const mapStateToProps: MapStateToProps<SiteSpaceRouterStateProps> = (state: AppState) => ({
  previousUrl: selectPreviousUrl(state),
  urls: siteUrlsSelector(state),
  routerReactKey: state.routerSpaceReactKey
});

export const ConnectedSiteSpaceRouter: React.ComponentClass<SiteSpaceRouterOwnProps>
  = connect(mapStateToProps)(SiteSpaceRouterComponent);

export interface SiteSpaceRouterProps extends SiteSpaceRouterOwnProps {
  readonly isBusy: boolean;
}

export class SiteSpaceRouter extends React.Component<SiteSpaceRouterProps> {
  public render() {
    if (this.props.isBusy) {
      // Render a blank page while we in are in middle of switching sites (isBusy===true)
      // In this point the url will contain the new site but we aren't ready to display
      // any data yet.
      return null;
    }

    return (
      <ConnectedReduxRouterErrorWrapper>
        <ConnectedSiteSpaceRouter
          siteName={this.props.siteName}
          routeLocationPath={this.props.routeLocationPath}
          routeLocationSearch={this.props.routeLocationSearch}
        />
      </ConnectedReduxRouterErrorWrapper>
    );
  }
}
